import React, { Component } from "react";
import { createPortal } from "react-dom";
import TrickOrTreat from "../../components/Halloween/TrickOrTreat";
import { CornerPumpkin } from "../../components/Halloween/Art";
import { HalloweenState, Play, fetchHalloween, trackHalloween } from "../../components/Halloween/api";

// Halloween's pop-up, and the little pumpkin in the corner that brings it back.
//
// It opens by itself once per visitor, a few seconds into their first page,
// and on a course page only as they leave (someone weighing up a course
// should not be interrupted mid-thought; the same rule the newsletter pop-up
// keeps). After that the pumpkin stays in the corner until the year's game
// ends, so anyone can play later or find their code again.

const OPEN_AFTER_MS = 8000;
const COURSE_SCROLL_RATIO = 0.7;
// One note per year, so next year's pop-up opens for people who saw this one.
const seenKey = (halloween: HalloweenState) => `42c_${halloween.key}_opened`;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

interface Props {
  pageContext?: "homepage" | "course" | "logged-in" | "checkout";
  pathname?: string;
}

interface State {
  halloween?: HalloweenState;
  open: boolean;
}

// Private browsing can refuse storage; the pop-up then simply asks again on
// a later visit, which is better than taking the page down.
const seenBefore = (halloween: HalloweenState) => {
  try {
    return window.localStorage.getItem(seenKey(halloween)) === "true";
  } catch (error) {
    return false;
  }
};

const markSeen = (halloween: HalloweenState) => {
  try {
    window.localStorage.setItem(seenKey(halloween), "true");
  } catch (error) {
    // Nothing to do.
  }
};

class HalloweenPopup extends Component<Props, State> {
  state: State = { open: false };
  private openTimer?: number;
  private dialogRef = React.createRef<HTMLDivElement>();
  private previouslyFocused: HTMLElement | null = null;
  private unmounted = false;
  // What they won while the pop-up was open. Held here and handed to state
  // on close: changing the game's props mid-spin would restart the wheel.
  private latestPlay: Play | null = null;
  private playChanged = false;

  componentDidMount() {
    fetchHalloween()
      .then((halloween) => {
        if (this.unmounted || !halloween.running) return;
        // Automated browsers (the deploy checks) get neither the pop-up nor
        // the corner pumpkin, which could otherwise sit over a button they
        // need to press. Real visitors are unaffected.
        if ((navigator as any).webdriver && !this.isPreview()) return;

        this.setState({ halloween });
        // The homepage's "Don't click this" button steps aside while the
        // pumpkin is in the corner (Chris, 27 Oct 2026): one surprise at a
        // time. It comes back by itself when the game ends.
        document.body.classList.add("has-halloween-pumpkin");

        if (this.isPreview()) {
          this.openPopup("preview");
          return;
        }
        if (this.shouldOpenByItself(halloween)) this.armTriggers();
      })
      .catch(() => {
        // A Halloween game is never worth an error message.
      });
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.disarmTriggers();
    document.removeEventListener("keydown", this.onKeyDown);
    document.body.classList.remove("has-halloween-popup");
    document.body.classList.remove("has-halloween-pumpkin");
  }

  isPreview = () => {
    try {
      return new URLSearchParams(window.location.search).get("preview_halloween") === "1";
    } catch (error) {
      return false;
    }
  };

  // Not for Pass holders (the server says), not for someone who has already
  // played or already seen it open, and never over a checkout, where it
  // would interrupt someone paying. The corner pumpkin still shows there, so
  // a code won earlier can be found again.
  shouldOpenByItself = (halloween: HalloweenState) =>
    halloween.popup && !halloween.play && !seenBefore(halloween) && this.props.pageContext !== "checkout";

  armTriggers = () => {
    if (this.props.pageContext === "course") {
      document.addEventListener("mouseout", this.onMouseOut);
      window.addEventListener("scroll", this.onScroll, { passive: true });
      return;
    }
    this.openTimer = window.setTimeout(() => this.openPopup("timer"), OPEN_AFTER_MS);
  };

  disarmTriggers = () => {
    window.clearTimeout(this.openTimer);
    document.removeEventListener("mouseout", this.onMouseOut);
    window.removeEventListener("scroll", this.onScroll);
  };

  onMouseOut = (event: MouseEvent) => {
    if (event.clientY > 0 || event.relatedTarget) return;
    this.openPopup("exit_intent");
  };

  onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    if (window.pageYOffset / scrollable < COURSE_SCROLL_RATIO) return;
    this.openPopup("scroll_depth");
  };

  openPopup = (trigger: string) => {
    if (this.state.open) return;

    this.disarmTriggers();
    if (trigger !== "preview" && this.state.halloween) markSeen(this.state.halloween);
    trackHalloween("open", { trigger });

    this.previouslyFocused = document.activeElement as HTMLElement;
    document.addEventListener("keydown", this.onKeyDown);
    document.body.classList.add("has-halloween-popup");
    this.setState({ open: true }, () => this.dialogRef.current?.focus());
  };

  closePopup = () => {
    document.removeEventListener("keydown", this.onKeyDown);
    document.body.classList.remove("has-halloween-popup");
    this.setState((state) => ({
      open: false,
      halloween: state.halloween && {
        ...state.halloween,
        play: this.playChanged ? this.latestPlay : state.halloween.play,
      },
    }));
    this.previouslyFocused?.focus({ preventScroll: true });
  };

  // Keeps what they won, so reopening from the corner shows the prize rather
  // than asking them to play again.
  onPlayed = (play: Play | null) => {
    this.latestPlay = play;
    this.playChanged = true;
  };

  onKeyDown = (event: globalThis.KeyboardEvent) => {
    if (event.key === "Escape") {
      this.closePopup();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = this.dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  render() {
    const { halloween, open } = this.state;
    if (!halloween) return null;

    return createPortal(
      <>
        {!open && (
          <button
            type="button"
            className={`hw-corner ${halloween.play ? "has-prize" : ""}`}
            onClick={() => this.openPopup("corner")}
            aria-label={halloween.play ? "Your Halloween treat" : "Trick or treat?"}
          >
            <CornerPumpkin />
            <span className="hw-corner__label">{halloween.play ? "Your treat" : "Trick or treat?"}</span>
          </button>
        )}
        {open && (
          <div className="hw-popup">
            <button
              className="hw-popup__backdrop"
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              onClick={this.closePopup}
            />
            <div
              className="hw-popup__card"
              role="dialog"
              aria-modal="true"
              aria-label="Trick or treat"
              ref={this.dialogRef}
              tabIndex={-1}
            >
              <TrickOrTreat
                halloween={halloween}
                variant="popup"
                onClose={this.closePopup}
                onPlayed={this.onPlayed}
              />
            </div>
          </div>
        )}
      </>,
      document.body,
    );
  }
}

export default HalloweenPopup;
